import React, { useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
    Menu,
    User,
    Users,
    GraduationCap,
    CreditCard,
    SlidersHorizontal,
    FileCheck,
} from "lucide-react";

import iiitbLogo from "../../images/IIITB_logo1.png";
import "./SidebarStudent.css";

import { useStudentProgress } from "../../context/StudentProgressContext";
import { useAdmissionDriveKind } from "../../hooks/useAdmissionDriveKind";

type SidebarProps = {
    sidebarCollapsed: boolean;
    toggleSidebar: () => void;
};

export default function SidebarStudent({
    sidebarCollapsed,
    toggleSidebar,
}: SidebarProps) {
    const navigate = useNavigate();
    const location = useLocation();
    const { progress, isAppSubmitted, refreshProgress } = useStudentProgress();

    const { kind: admissionKind, loading: admissionDriveLoading, isDriveEditOpen } =
        useAdmissionDriveKind();

    useEffect(() => {
        refreshProgress()
    }, [])

    // ✅ Base menu
    const baseMenuItems = [
        { text: "Personal Details", path: "/personaldetails", icon: <User size={22} />, id: "personal" },
        { text: "Parent & Guardians Details", path: "/parentdetails", icon: <Users size={22} />, id: "parent" },
        { text: "Education Details", path: "/educationdetails", icon: <GraduationCap size={22} />, id: "education" },
    ];

    // ✅ Conditionally add Exam Details (ONLY for BTech)
    const examItem =
        admissionKind === "btech"
            ? [
                {
                    text: "Exam Details",
                    path: "/examdetails",
                    icon: <FileCheck size={22} />,
                    id: "exam",
                },
            ]
            : [];

    const programmeItem = [
        {
            text: "Programme Details",
            path: "/programmedetails",
            icon: <SlidersHorizontal size={22} />,
            id: "programme",
        },
    ];

    const paymentItem = [
        {
            text: "Pay & Submit",
            path: "/paymentinitiate",
            icon: <CreditCard size={22} />,
            id: "payment",
        },
    ];

    // ✅ Final menu (order matters)
    const menuItems = [
        ...baseMenuItems,
        ...examItem,        // 👈 inserted before programme
        ...programmeItem,
        ...paymentItem,
    ];

    const isEnabled = (id: string) => {
        if (progress.loading || admissionDriveLoading) return false;
        // console.log("isAppSubmitted", isAppSubmitted, "isDriveEditOpen", isDriveEditOpen)
        switch (id) {
            case "personal":

                return (isAppSubmitted && isDriveEditOpen) ? false : true;

            case "parent":
                return (isAppSubmitted && isDriveEditOpen) ? false : progress.personalDetails;

            case "education":
                return (isAppSubmitted && isDriveEditOpen) ? false : progress.parentDetails;

            case "exam":
                return (isAppSubmitted && isDriveEditOpen) ? true : progress.educationDetails;

            case "programme":
                // 👇 depends on whether exam step exists
                return (isAppSubmitted && isDriveEditOpen) ? false : admissionKind === "btech"
                    ? progress.examDetails
                    : progress.educationDetails;

            case "payment":
                return (isAppSubmitted && isDriveEditOpen) ? false : progress.programmeDetails;

            default:
                return false;
        }
    };

    return (
        <aside className={`yt-sidebar-student ${sidebarCollapsed ? "collapsed" : ""}`}>
            {/* TOP ROW */}
            <div className="yt-top">
                <button
                    className="yt-menu-btn"
                    onClick={toggleSidebar}
                    type="button"
                    aria-label="Toggle sidebar"
                >
                    <Menu size={22} />
                </button>

                {!sidebarCollapsed && (
                    <img
                        src={iiitbLogo}
                        alt="IIITB"
                        className="yt-logo-inline"
                        onClick={() => navigate("/personaldetails")}
                    />
                )}
            </div>

            {/* INSTITUTE NAME */}
            {!sidebarCollapsed && (
                <div className="yt-institute">
                    International Institute of <br />
                    Information Technology <br />
                    Bangalore
                </div>
            )}

            {/* NAV ITEMS */}
            <nav className="yt-nav">
                {menuItems.map((item) => {
                    const active = location.pathname === item.path;
                    const enabled = isEnabled(item.id);

                    return (
                        <button
                            key={item.text}
                            className={`yt-item ${active ? "active" : ""} ${sidebarCollapsed ? "icon-only" : ""
                                } ${!enabled ? "disabled" : ""}`}
                            onClick={() => enabled && navigate(item.path)}
                            type="button"
                            disabled={!enabled}
                            style={{
                                opacity: enabled ? 1 : 0.5,
                                cursor: enabled ? "pointer" : "not-allowed",
                            }}
                        >
                            <span className="yt-icon">{item.icon}</span>

                            {!sidebarCollapsed && (
                                <span className="yt-text">{item.text}</span>
                            )}
                        </button>
                    );
                })}
            </nav>
        </aside>
    );
}